import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowLeft, Car, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Mileage() {
  const { business } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), starting_location: "", destination: "", purpose: "", miles: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mileage");
      setItems(data);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/mileage", { ...form, miles: parseFloat(form.miles || 0) });
      toast.success("Trip added");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), starting_location: "", destination: "", purpose: "", miles: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    try { await api.delete(`/mileage/${id}`); load(); }
    catch { toast.error("Failed"); }
  };

  const totalMiles = items.reduce((s, t) => s + Number(t.miles || 0), 0);
  const rate = business?.mileage_rate || 0.67;

  // Group by month for summary
  const byMonth = {};
  items.forEach((t) => {
    const ym = (t.date || "").slice(0, 7);
    byMonth[ym] = (byMonth[ym] || 0) + Number(t.miles || 0);
  });

  const now = new Date();
  const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const ytdMiles = items
    .filter((t) => (t.date || "").startsWith(String(now.getFullYear())))
    .reduce((s, t) => s + Number(t.miles || 0), 0);

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-4"><Link to="/app/financials" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Financials</Link></div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Financials</div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3"><Car className="w-7 h-7 text-[#FF5A00]"/> Mileage</h1>
          <div className="text-sm text-zinc-500 mt-1">Rate ${rate}/mi · edit in <Link className="text-[#FF5A00] hover:underline" to="/app/settings">Settings</Link></div>
        </div>
        <Button onClick={()=>setOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="mileage-add-btn"><Plus className="w-4 h-4 mr-1"/> Add trip</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SummaryCard label="This month" miles={byMonth[thisMonthKey] || 0} rate={rate} testid="mileage-summary-this-month"/>
        <SummaryCard label="Year to date" miles={ytdMiles} rate={rate} testid="mileage-summary-ytd"/>
        <SummaryCard label="All time" miles={totalMiles} rate={rate} testid="mileage-summary-total"/>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          <div className="col-span-2">Date</div>
          <div className="col-span-3">From</div>
          <div className="col-span-3">To</div>
          <div className="col-span-2">Purpose</div>
          <div className="col-span-1 text-right">Miles</div>
          <div className="col-span-1 text-right">•</div>
        </div>
        {loading ? <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
        : items.length === 0 ? <div className="p-12 text-center text-zinc-400">No trips yet.</div>
        : (
          <div className="divide-y divide-zinc-100">
            {items.map((t) => (
              <div key={t.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center" data-testid={`mileage-row-${t.id}`}>
                <div className="col-span-2 text-sm font-mono-accent text-zinc-600">{(t.date || "").slice(0, 10)}</div>
                <div className="col-span-3 text-sm truncate">{t.starting_location || "—"}</div>
                <div className="col-span-3 text-sm truncate">{t.destination || "—"}</div>
                <div className="col-span-2 text-sm text-zinc-600 truncate">{t.purpose || "—"}</div>
                <div className="col-span-1 text-right text-sm font-mono-accent font-semibold">{Number(t.miles).toFixed(1)}</div>
                <div className="col-span-1 flex justify-end"><button onClick={()=>del(t.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`mileage-delete-${t.id}`}><Trash2 className="w-4 h-4"/></button></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add mileage trip</DialogTitle>
            <DialogDescription>Enter a single trip or a monthly total (use one row per month with combined miles).</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} data-testid="mileage-date-input"/></div>
            <div><Label>Miles</Label><Input type="number" step="0.1" required value={form.miles} onChange={(e)=>setForm({...form, miles: e.target.value})} data-testid="mileage-miles-input"/></div>
            <div><Label>Starting location</Label><Input value={form.starting_location} onChange={(e)=>setForm({...form, starting_location: e.target.value})} data-testid="mileage-start-input"/></div>
            <div><Label>Destination</Label><Input value={form.destination} onChange={(e)=>setForm({...form, destination: e.target.value})} data-testid="mileage-dest-input"/></div>
            <div className="col-span-2"><Label>Business purpose</Label><Input value={form.purpose} onChange={(e)=>setForm({...form, purpose: e.target.value})} data-testid="mileage-purpose-input"/></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="mileage-notes-input"/></div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="mileage-save-btn">{busy ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, miles, rate, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-5" data-testid={testid}>
      <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-2xl font-bold">{miles.toFixed(1)} <span className="text-sm font-normal text-zinc-500">mi</span></div>
      <div className="text-sm text-emerald-600 font-mono-accent mt-1">${(miles * rate).toFixed(2)}</div>
    </div>
  );
}
